import Lottie from 'lottie-react';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import confettiAnimation from '../../assets/animations/confetti.json';
import { CheckIcon, ChevronDownIcon } from '../../assets/icons/index';
import smilingFaceImage from '../../assets/images/smiling-face.png';
import { AppFormattedMessage } from '../../components/app-formatted-message/index';
import Button from '../../components/button/index';
import Loader from '../../components/loader/index';
import { ReactPortal } from '../../components/modal/react-portal.util';
import { QueryKey } from '../../constants/index';
import { Company, useInvalidateQueries, User } from '../../hooks/index';
import { payedInternallyStore } from '../../storage/local/index';
import { PageRoute } from '../../types/page.types';
import { StakeholderRole } from '../../types/stakeholder.types';

export const WelcomePage: FC = () => {
  const [visibleAnimation, setVisibleAnimation] = useState(true);
  const { selectedCompany } = Company.useSelected();
  const role = selectedCompany?.roleInCompany;
  const navigate = useNavigate();
  const { invalidateQuery } = useInvalidateQueries(
    QueryKey.GET_CLIENT_SECRET,
    QueryKey.GET_SELECTED_COMPANY,
    QueryKey.GET_COMPANIES,
  );

  const { user, isLoading } = User.useUser();

  const handleGoToAddStakeholders = useCallback(() => {
    invalidateQuery();
    if (role !== StakeholderRole.STAKEHOLDER) {
      setTimeout(() => {
        navigate(PageRoute.ONBOARDING);
      }, 500);
    } else {
      navigate(PageRoute.MY_HOLDINGS);
    }
  }, [invalidateQuery, navigate, role]);

  useEffect(() => {
    if (selectedCompany) {
      payedInternallyStore.set({
        ...(payedInternallyStore.get() || {}),
        [selectedCompany.id]: true,
      });
    }
  }, [selectedCompany]);

  if (isLoading || !user)
    return (
      <ReactPortal initialPortalId="modal">
        <Loader className="absolute left-0 top-0 z-[999999999] h-screen w-screen pl-10 pt-10 max-lg:pl-16 max-lg:pt-36" />
      </ReactPortal>
    );

  return (
    <>
      <div className="m-auto flex h-fit min-h-full w-full max-w-[400px] flex-col items-center gap-10">
        <div className="flex size-28 shrink-0 items-center justify-center rounded-full border-[12px] border-gray-50 bg-gradient-to-t from-forest-600 to-forest-300 shadow-md">
          <CheckIcon height={'38px'} iconColor="#ffffff" width={'38px'} />
        </div>
        <div className="flex flex-col gap-4 text-center">
          <span
            className="!bg-clip-text text-4xl-mobile font-bold text-transparent lg:text-4xl"
            style={{
              background: 'linear-gradient(132.59deg, #475467 29.58%, #101828 84.27%)',
            }}
          >
            <AppFormattedMessage id="WELCOME_ABOARD" />
          </span>
          <div className="flex flex-col gap-2">
            <span className="text-sm font-[450] text-gray-500">
              <AppFormattedMessage id="SENDED_RECEIPT_TO_EMAIL" />
            </span>
            <span className="text-sm font-medium text-gray-700">{user.email}</span>
          </div>
        </div>
        <div className="flex flex-col gap-8 border-t-[1px] border-gray-200 pt-10 text-center">
          <span className="text-sm font-[450] text-gray-700">
            <AppFormattedMessage id="GREETING_COMPANY_SETUP" />
          </span>
          <Button
            className="w-full bg-brand-700 px-4 py-[10px] text-sm font-[550] text-white"
            onClick={handleGoToAddStakeholders}
          >
            Next Steps
          </Button>
          <div className="-mt-4 flex items-center justify-between rounded-xl border-[1px] border-gray-200 px-3 py-2">
            <div className="flex cursor-pointer items-center gap-4">
              <div className="flex !size-10 shrink-0 items-center justify-center rounded-lg border-[1px] border-gray-200">
                <img alt="" className="size-[20px]" src={smilingFaceImage} />
              </div>
              <a
                className="flex flex-col gap-2 text-start"
                href={process.env.REACT_APP_SCHEDULE_CALL_URL}
                rel="noreferrer noopener nofollow"
                target="_blank"
              >
                <span className="text-sm font-[550] text-[#172335]">
                  <AppFormattedMessage id="HELP_ME_SETUP" />
                </span>
                <span className="text-label-md font-normal text-gray-600">
                  Schedule a call, and we'll help you with your setup!
                </span>
              </a>
            </div>
            <Button
              className="h-fit w-fit -rotate-90 rounded p-1 transition-colors hover:bg-gray-100"
              styleType="NONE"
            >
              <ChevronDownIcon height={'16px'} iconColor="#172335" width={'16px'} />
            </Button>
          </div>
        </div>
        {visibleAnimation && (
          <Lottie
            animationData={confettiAnimation}
            className="absolute bottom-0 z-50 h-screen w-full max-lg:translate-y-[5%] max-lg:scale-105"
            loop={false}
            onComplete={() => setVisibleAnimation(false)}
          />
        )}
      </div>
    </>
  );
};
